import type { StudentDto } from '@mm/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { listStudents } from '../api/students';
import { AppLayout } from '../layout/AppLayout';
import { Button } from '../ui/Button';
import { IconChevronRight, IconPlus } from '../ui/icons';

export function HomePage() {
  const { user } = useAuth();
  const [totalStudents, setTotalStudents] = useState<number | null>(null);
  const [recent, setRecent] = useState<StudentDto[]>([]);

  useEffect(() => {
    listStudents({ sort: 'updatedAt', order: 'desc', pageSize: 5 }).then((r) => {
      setTotalStudents(r.total);
      setRecent(r.items);
    });
  }, []);

  const greeting =
    new Date().getHours() < 6
      ? 'Доброй ночи'
      : new Date().getHours() < 12
        ? 'Доброе утро'
        : new Date().getHours() < 18
          ? 'Добрый день'
          : 'Добрый вечер';

  const firstName = user?.name.split(' ')[0] ?? '';

  return (
    <AppLayout crumbs={[{ label: 'Админ' }, { label: 'Главная' }]}>
      <div className="max-w-5xl px-8 py-10">
        <h1
          className="font-display text-[52px] leading-none font-medium mb-2"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 15' }}
        >
          {greeting}, {firstName}
        </h1>
        <p className="font-caps italic text-[14px] text-muted mb-10">
          Рабочее место репетитора по математике
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border rounded border border-border overflow-hidden mb-10">
          <Stat
            title="Учеников в базе"
            value={totalStudents ?? '…'}
            to="/app/students"
          />
          <Stat title="Досок всего" value="—" hint="появится, когда начнёте преподавать" />
          <Stat title="Активность 7 дней" value="—" hint="обновлённые доски" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <h2
                className="font-display text-[22px] leading-none font-medium"
                style={{ fontVariationSettings: '"opsz" 48, "SOFT" 10' }}
              >
                Недавние ученики
              </h2>
              <Link to="/app/students" className="text-[12px] text-muted hover:text-accent flex items-center gap-1">
                все <IconChevronRight size={12} />
              </Link>
            </div>
            {recent.length === 0 ? (
              <div className="rounded border-2 border-dashed border-border p-8 text-center">
                <span
                  className="block font-display text-[48px] leading-none text-border mb-1"
                  style={{ fontVariationSettings: '"opsz" 96' }}
                >
                  ∅
                </span>
                <p className="text-muted text-[13px] mb-3">У вас пока нет учеников.</p>
                <Link to="/app/students/new">
                  <Button size="sm">
                    <IconPlus size={12} />
                    Добавить первого
                  </Button>
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-border-soft border-t border-b border-border-soft">
                {recent.map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/app/students/${s.id}`}
                      className="flex items-center justify-between py-2.5 px-1 hover:bg-row-hover rounded transition-colors"
                    >
                      <span className="min-w-0">
                        <span className="block font-medium text-[14px] text-fg truncate">{s.fullName}</span>
                        <span className="block text-[12px] text-muted">
                          {s.grade ? (
                            <span className="font-serif-num">{s.grade}</span>
                          ) : (
                            <span>—</span>
                          )}
                          {s.email ? (
                            <>
                              {' · '}
                              <span>{s.email}</span>
                            </>
                          ) : null}
                        </span>
                      </span>
                      <span className="text-[11px] text-muted nums whitespace-nowrap pl-3">
                        {new Date(s.updatedAt).toLocaleDateString('ru')}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2
              className="font-display text-[22px] leading-none font-medium mb-3"
              style={{ fontVariationSettings: '"opsz" 48, "SOFT" 10' }}
            >
              Быстрые действия
            </h2>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/app/students/new"
                  className="flex items-center justify-between rounded border border-border hover:border-muted px-4 py-3 transition-colors"
                >
                  <span>
                    <span className="block font-medium text-[14px]">Новый ученик</span>
                    <span className="block text-[12px] text-muted">Добавить подопечного в базу</span>
                  </span>
                  <IconChevronRight size={14} className="text-muted" />
                </Link>
              </li>
              <li>
                <Link
                  to="/app/students"
                  className="flex items-center justify-between rounded border border-border hover:border-muted px-4 py-3 transition-colors"
                >
                  <span>
                    <span className="block font-medium text-[14px]">Открыть учеников</span>
                    <span className="block text-[12px] text-muted">Список с поиском и сортировкой</span>
                  </span>
                  <IconChevronRight size={14} className="text-muted" />
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}

function Stat({
  title,
  value,
  hint,
  to,
}: {
  title: string;
  value: string | number;
  hint?: string;
  to?: string;
}) {
  const content = (
    <div className="bg-bg p-5 h-full">
      <div className="font-caps italic text-[11px] uppercase tracking-[0.14em] text-muted mb-1">
        {title}
      </div>
      <div
        className="font-display text-[40px] leading-none text-fg nums"
        style={{ fontVariationSettings: '"opsz" 96, "SOFT" 0' }}
      >
        {value}
      </div>
      {hint ? <p className="text-[11px] text-muted mt-1">{hint}</p> : null}
    </div>
  );
  return to ? (
    <Link to={to} className="hover:bg-row-hover transition-colors">
      {content}
    </Link>
  ) : (
    content
  );
}
